import React from "react";
import {
    FormCreationData,
    DeviceType,
} from "../../../../../../stateManager/store/formCreation_v2/types";
import { Flex } from "./../Flex";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedFormItemId } from "../../../../../../stateManager/store/formCreation_v2/actions";
import { clearSelectedItems } from "../../../../../../stateManager/store/contextMenu/action";
import { getPadding, onDrop } from "../../helpers";
import { AppState } from "../../../../../../stateManager/store";
import { ReactComponent as Plus } from "../../../../../../assets/svg/flatlistplus.svg";
import { getMargin } from "../../helpers/generateStyles";
import { avatartemp2, columntemp2, datatemp2, emailTexttemp2, nameTexttemp2 } from "../templates/FlatlistTemplates";

interface IFlatList {
    isCoreV3: boolean;
    element: FormCreationData;
    deviceType: DeviceType;
    isCheckedIn: Boolean;
}

export const FlatList: React.FC<IFlatList> = (props) => {
    const { element, deviceType, isCheckedIn, isCoreV3 } = props;
    const dispatch = useDispatch();
    const { properties, fields, isChildAsset, isAsset, label } = element;
    const style = properties.styles[deviceType];
    const { width, widthMode, height, heightMode, gap, backgroundColor, visible } = style;
    const { components } = useSelector((state: AppState) => state.formJson)
    const paddingStyle = getPadding(style);
    const marginStyle = getMargin(style);
    const visibilityGen = `${visible === true ? "block" : visible ? "block" : "hidden"}`;

    const getHeight = (): string => {
        let returnVal = "100%";
        if (heightMode === 'auto') {
            returnVal = "auto"
        } else if (heightMode === 'min-content') {
            returnVal = "min-content"
        } else if (heightMode === 'max-content') {
            returnVal = "max-content"
        } else if (heightMode === '%' || heightMode === 'px') {
            returnVal = `${height}${heightMode}`
        } else {
            if (deviceType === "Sm") {
                returnVal = "100%"
            } else {
                returnVal = "700px"
            }
        }
        return returnVal
    }

    const getWidth = (): string => {
        if (widthMode === 'auto') {
            return "auto"
        } else if (widthMode === 'min-content') {
            return "min-content"
        } else if (widthMode === 'max-content') {
            return "max-content"
        } else if (widthMode === '%' || widthMode === 'px') {
            return `${width}${widthMode}`
        }
        return ""
    }

    const getFlexClassName = (style: any) => {
        let flexDirection = ""
        if (style.flexDirection === "row") {
            flexDirection = "flex-row";
        } else if (style.flexDirection === "column") {
            flexDirection = "flex-col"
        } else {
            flexDirection = "flex-col"
        }
        // Align Items
        let alignItems = "";
        if (style.flexAlignItems === "Normal") {
            alignItems = "";
        } else if (style.flexAlignItems === "Center") {
            alignItems = "items-center"
        } else if (style.flexAlignItems === "Flex Start") {
            alignItems = "items-start"
        } else if (style.flexAlignItems === "Flex End") {
            alignItems = "items-end"
        } else if (style.flexAlignItems === "Stretch") {
            alignItems = "items-stretch"
        } else if (style.flexAlignItems === "Baseline") {
            alignItems = "items-baseline"
        }
        // Justify Content
        let justifyContent = "";
        if (style.flexJustifyContent === "Normal") {
            justifyContent = "";
        } else if (style.flexJustifyContent === "Center") {
            justifyContent = "justify-center"
        } else if (style.flexJustifyContent === "Flex Start") {
            justifyContent = "justify-start"
        } else if (style.flexJustifyContent === "Flex End") {
            justifyContent = "justify-end"
        } else if (style.flexJustifyContent === "Space Between") {
            justifyContent = "justify-between"
        } else if (style.flexJustifyContent === "Space Around") {
            justifyContent = "justify-around"
        } else if (style.flexJustifyContent === "Space Evenly") {
            justifyContent = "justify-evenly"
        }
        return `flex 
              ${flexDirection} 
              ${style.flexWrap ? "flex-wrap" : "flex-nowrap"} 
              ${alignItems && alignItems} 
              ${justifyContent && justifyContent}`;
    }

    const dynamicClassName = getFlexClassName(style);

    const handleSelect = (e: React.MouseEvent<HTMLDivElement>, id: string) => {
        e.stopPropagation();
        dispatch(
            setSelectedFormItemId({
                id,
                isAssetInstanceSelected: isAsset || isChildAsset,
            })
        );
        dispatch(clearSelectedItems())
    };

    const handleAddTemplate = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        // Default template design for Flat list
        const rowId: any = onDrop({ id: element.id }, datatemp2, deviceType, isCoreV3)
        onDrop({ id: rowId }, avatartemp2, deviceType, isCoreV3)
        const colId: any = onDrop({ id: rowId }, columntemp2, deviceType, isCoreV3)
        onDrop({ id: colId }, nameTexttemp2, deviceType, isCoreV3)
        onDrop({ id: colId }, emailTexttemp2, deviceType, isCoreV3)
    }

    return (
        isCoreV3 ?
            <div
                id={`FlatList-${element.id}`}
                className={`${element.type}-wrapper ${visibilityGen} flatlist-component relative w-full`}
                onClick={(e) => handleSelect(e, element.id)}
                style={{
                    height: getHeight(),
                    borderRadius: "4px",
                    border: "1px solid #D9D9D9",
                    flexShrink: 0,
                    maxHeight: getHeight(),
                    width: getWidth(),
                    backgroundColor: backgroundColor ? `${backgroundColor}` : '#fff',
                }}
            >
                <button className="absolute top-0 right-0 p-2 mr-1 cursor-pointer outline-none"
                    onClick={handleAddTemplate}
                >
                    <Plus style={{ width: "18px", height: "18px" }} />
                </button>
                <div className="h-full overflow-auto max-h-full w-full">
                    <div className="text-base font-semibold text-black mt-[5px]">{label}</div>
                    <div className={`${dynamicClassName} flatlist-fields-wrapper mt-8`}
                        style={{
                            height: "100%",
                            overflow: "auto",
                            maxHeight: "100%",
                            width: "100%",
                            flexShrink: 0,
                            gap: `${gap}px`,
                            ...paddingStyle,
                            ...marginStyle
                        }}
                    >
                        {element.fields?.map((templateId, idx) => {
                            const template = components[templateId]
                            return (
                                <div key={idx} className="p-1">
                                    <Flex isCheckedIn={true} deviceType={deviceType} element={template} minHeight={80} />
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div> : null
    );
};
